import { Language } from "../languages/labels/language.labels"
import { dateFormat } from "../scripts/date.time.format.script"

function toLocalIsoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

export function taxStatementMonthlyCompanyList(lang: Language, _company_created: string): {code: string, display: string}[] {
  let currentYear = new Date().getFullYear()

  const generateForYear = (year: number): {code: string, display: string}[] => Array.from({length: 12}, (_, i) => {
    let month = i + 1
    let startIso = toLocalIsoDate(year, month, 1)
    let endIso = toLocalIsoDate(year, month, lastDayOfMonth(year, month))
    let deadlineMonth = ((month + 2 - 1) % 12) + 1
    let deadlineYear = year + Math.floor((month + 2 - 1) / 12)
    let deadlineIso = toLocalIsoDate(deadlineYear, deadlineMonth, lastDayOfMonth(deadlineYear, deadlineMonth))

    return {code: `${month},${year}`, display: `${month}° ${lang.labels.month.toLowerCase()} ${year} (${dateFormat(lang.code, startIso)} - ${dateFormat(lang.code, endIso)}) | ${lang.labels.deadline}: ${dateFormat(lang.code, deadlineIso)}`}
  })

  return [...generateForYear(currentYear - 1), ...generateForYear(currentYear), ...generateForYear(currentYear + 1)]
}

export function taxStatementQuarterlyCompanyList(lang: Language, _company_created: string): {code: string, display: string}[] {
  let quarters = [{quarter: 1, start: 1, end: 3}, {quarter: 2, start: 4, end: 6}, {quarter: 3, start: 7, end: 9}, {quarter: 4, start: 10, end: 12}]
  let currentYear = new Date().getFullYear()

  const generateForYear = (year: number): {code: string, display: string}[] => quarters.map(({quarter, start, end}) => {
    let startIso = toLocalIsoDate(year, start, 1)
    let endIso = toLocalIsoDate(year, end, lastDayOfMonth(year, end))
    let deadlineMonth = ((end + 2 - 1) % 12) + 1
    let deadlineYear = year + Math.floor((end + 2 - 1) / 12)
    let deadlineIso = toLocalIsoDate(deadlineYear, deadlineMonth, lastDayOfMonth(deadlineYear, deadlineMonth))

    return {code: `${quarter},${year}`, display: `${quarter}° ${lang.labels.quarter.toLowerCase()} ${year} (${dateFormat(lang.code, startIso)} - ${dateFormat(lang.code, endIso)}) | ${lang.labels.deadline}: ${dateFormat(lang.code, deadlineIso)}`}
  })

  return [...generateForYear(currentYear - 1), ...generateForYear(currentYear), ...generateForYear(currentYear + 1)]
}

export function taxStatementSemiannualCompanyList(lang: Language, _company_created: string): {code: string, display: string}[] {
  let semesters = [{semester: 1, start: 1, end: 6}, {semester: 2, start: 7, end: 12}]
  let currentYear = new Date().getFullYear()

  const generateForYear = (year: number): {code: string, display: string}[] => semesters.map(({semester, start, end}) => {
    let startIso = toLocalIsoDate(year, start, 1)
    let endIso = toLocalIsoDate(year, end, lastDayOfMonth(year, end))
    let deadlineMonth = ((end + 2 - 1) % 12) + 1
    let deadlineYear = year + Math.floor((end + 2 - 1) / 12)
    let deadlineIso = toLocalIsoDate(deadlineYear, deadlineMonth, lastDayOfMonth(deadlineYear, deadlineMonth))

    return {code: `${semester},${year}`,
      display: `${semester}° ${lang.labels.semester.toLowerCase()} ${year} (${dateFormat(lang.code, startIso)} - ${dateFormat(lang.code, endIso)}) | ${lang.labels.deadline}: ${dateFormat(lang.code, deadlineIso)}`}
  })

  return [...generateForYear(currentYear - 1), ...generateForYear(currentYear), ...generateForYear(currentYear + 1)]
}

export function taxStatementAnnualCompanyList(lang: Language, _company_created: string): {code: string, display: string}[] {
  let currentYear = new Date().getFullYear()

  const generateForYear = (year: number): {code: string, display: string}[] => {
    let startIso = toLocalIsoDate(year, 1, 1)
    let endIso = toLocalIsoDate(year, 12, 31)
    let deadlineIso = toLocalIsoDate(year + 1, 2, lastDayOfMonth(year + 1, 2))

    return [{
      code: `1,${year}`,
      display: `${year} (${dateFormat(lang.code, startIso)} - ${dateFormat(lang.code, endIso)}) | ${lang.labels.deadline}: ${dateFormat(lang.code, deadlineIso)}`
    }]
  }

  return [...generateForYear(currentYear - 1), ...generateForYear(currentYear), ...generateForYear(currentYear + 1)]
}

export function taxStatementCompanyList(lang: Language, company_created: string, tax_period: string): {code: string, display: string}[] {
  switch (tax_period) {
    case 'monthly':
      return taxStatementMonthlyCompanyList(lang, company_created)
    case 'quarterly':
      return taxStatementQuarterlyCompanyList(lang, company_created)
    case 'semiannual':
      return taxStatementSemiannualCompanyList(lang, company_created)
    case 'annual':
      return taxStatementAnnualCompanyList(lang, company_created)
    default:
      return []
  }
}

export function taxStatementCompanyValue(lang: Language, company_created: string, tax_period: string, code: string): string {
  let taxStatement = taxStatementCompanyList(lang, company_created, tax_period).find((item) => (item.code === code))
  return taxStatement ? taxStatement.display : lang.labels.undefined
}
